import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { alertKeys } from "./alerts.js";
import { API_BASE } from "./client.js";
import { dashboardLayoutKey } from "./dashboard.js";
import { packageKeys } from "./packages.js";
import { keys } from "./queries.js";
import { userKeys } from "./users.js";

/** Every event concerns one project; its queries live under the `projects` key. */
const EVENT_TYPES = [
  "connection.synced",
  "connection.failed",
  "resource.created",
  "resource.changed",
  "resource.deleted",
  "operation.progress",
  "operation.finished",
  "activity.created",
] as const;

/**
 * Subscribes to the API's event stream while signed in and refreshes the
 * affected queries. EventSource reconnects by itself and sends Last-Event-ID.
 */
export function useLiveUpdates(enabled: boolean): "live" | "reconnecting" | "off" {
  const client = useQueryClient();
  const [state, setState] = useState<"live" | "reconnecting" | "off">("off");

  useEffect(() => {
    if (!enabled) {
      setState("off");
      return;
    }
    const source = new EventSource(`${API_BASE}/events`, { withCredentials: true });
    source.onopen = () => setState("live");
    source.onerror = () => setState("reconnecting");
    const refresh = () => client.invalidateQueries({ queryKey: keys.projects });
    for (const type of EVENT_TYPES) source.addEventListener(type, refresh);
    // A moved dashboard object changes only that project's layout; nothing else is refetched.
    source.addEventListener("dashboard.changed", (event) => {
      const { projectId } = JSON.parse((event as MessageEvent<string>).data) as {
        projectId?: string;
      };
      if (projectId) void client.invalidateQueries({ queryKey: dashboardLayoutKey(projectId) });
    });
    // The cost meter wrote a new hour: that project's costs and the organisation's.
    source.addEventListener("costs.updated", (event) => {
      const { projectId } = JSON.parse((event as MessageEvent<string>).data) as {
        projectId?: string;
      };
      void client.invalidateQueries({ queryKey: ["costs"] });
      if (projectId) void client.invalidateQueries({ queryKey: ["projects", projectId, "costs"] });
    });
    // A budget, its raise requests or its alerts changed.
    source.addEventListener("budget.changed", (event) => {
      const { projectId } = JSON.parse((event as MessageEvent<string>).data) as {
        projectId?: string;
      };
      void client.invalidateQueries({ queryKey: ["budgets"] });
      void client.invalidateQueries({ queryKey: ["costs"] });
      if (projectId) void client.invalidateQueries({ queryKey: ["projects", projectId] });
    });
    // The stream passes on only the signed-in person's own notifications.
    source.addEventListener("notification.created", () => {
      void client.invalidateQueries({ queryKey: ["notifications"] });
      // Approval requests and decisions come as notifications (docs/approvals.md).
      void client.invalidateQueries({ queryKey: ["approvals"] });
    });
    // Alerts of a project fired, resolved or were muted; someone got in-panel notifications.
    source.addEventListener("alert.changed", (event) => {
      const { projectId } = JSON.parse((event as MessageEvent<string>).data) as {
        projectId?: string;
      };
      if (projectId) void client.invalidateQueries({ queryKey: alertKeys.project(projectId) });
      void client.invalidateQueries({ queryKey: alertKeys.accountAlerts });
    });
    // A package instance, a server's «Руки прочь» or an Autopilot card changed (docs/packages.md).
    source.addEventListener("package.changed", (event) => {
      const { projectId } = JSON.parse((event as MessageEvent<string>).data) as {
        projectId?: string;
      };
      if (projectId) void client.invalidateQueries({ queryKey: packageKeys.all(projectId) });
    });
    source.addEventListener("alert.notified", () => {
      void client.invalidateQueries({ queryKey: alertKeys.inbox });
    });
    // Someone's role or access changed: the person's own rights (who they are, which
    // projects they see) and the users section are read again; the API already applies them.
    source.addEventListener("access.changed", () => {
      for (const queryKey of [keys.auth, keys.projects, userKeys.users, userKeys.invitations])
        void client.invalidateQueries({ queryKey });
    });
    return () => source.close();
  }, [enabled, client]);

  return state;
}
